import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  LinearProgress,
  TextField,
} from '@mui/material'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import { useEffect, useState } from 'react'
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import {
  DASHBOARD_COLORS,
  dashboardCardSx,
  formatCurrency,
  formatNumber,
} from './dashboardFormatters'
import RangeButtons, { ChartLegend } from './RangeButtons'

const ORDER_STATUS_COLORS = [
  DASHBOARD_COLORS.green,
  DASHBOARD_COLORS.amber,
  DASHBOARD_COLORS.red,
  DASHBOARD_COLORS.blue,
]

const SALES_OVERVIEW_SERIES = {
  sales: {
    label: 'Sales (৳)',
    color: DASHBOARD_COLORS.red,
    fill: 'rgba(208,2,27,0.25)',
  },
  orders: {
    label: 'Orders',
    color: DASHBOARD_COLORS.blue,
    fill: 'rgba(41,128,185,0.15)',
  },
}

const SalesOverviewTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null

  const byKey = Object.fromEntries(payload.map((entry) => [entry.dataKey, entry]))

  return (
    <Box
      sx={{
        bgcolor: '#1a1a1a',
        color: '#fff',
        px: 1.5,
        py: 1.25,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        fontSize: '12px',
        minWidth: 168,
      }}
    >
      <Typography sx={{ fontWeight: 700, mb: 0.75, fontSize: '12px', color: '#fff' }}>
        {label}
      </Typography>
      {['sales', 'orders'].map((key) => {
        const entry = byKey[key]
        const meta = SALES_OVERVIEW_SERIES[key]
        if (!entry || !meta) return null

        const value = key === 'orders'
          ? formatNumber(entry.value)
          : formatCurrency(entry.value)

        return (
          <Stack
            key={key}
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 0.35, '&:last-of-type': { mb: 0 } }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                bgcolor: meta.fill,
                border: `1.5px solid ${meta.color}`,
                flexShrink: 0,
              }}
            />
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '12px', color: '#fff' }}>
              {meta.label}:
            </Typography>
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '12px', color: '#fff' }}>
              {value}
            </Typography>
          </Stack>
        )
      })}
    </Box>
  )
}

export const OrderStatusBlock = ({ data, loading, compact = false }) => {
  const segments = data?.segments || []
  const showLoading = loading && segments.length === 0
  return (
    <>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 1,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: 'text.secondary',
          textTransform: 'uppercase',
          fontSize: '11px',
        }}
      >
        Order Status
      </Typography>
      {showLoading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ height: compact ? 110 : 120 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              innerRadius={compact ? 32 : 35}
              outerRadius={compact ? 50 : 55}
              paddingAngle={2}
            >
              {segments.map((_, i) => (
                <Cell key={i} fill={ORDER_STATUS_COLORS[i % ORDER_STATUS_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
        {segments.map((s, i) => (
          <Stack key={s.label} direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '2px',
                bgcolor: ORDER_STATUS_COLORS[i % ORDER_STATUS_COLORS.length],
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {s.label} {s.pct}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </>
  )
}

export const SalesOverviewSection = ({
  data,
  salesRange,
  salesStart,
  salesEnd,
  onRangeChange,
  onCustomRangeChange,
  loading,
  timezone,
}) => {
  const [customMode, setCustomMode] = useState(salesRange === 'custom')
  const [customStart, setCustomStart] = useState(salesStart || '')
  const [customEnd, setCustomEnd] = useState(salesEnd || '')

  useEffect(() => {
    if (salesRange === 'custom') setCustomMode(true)
  }, [salesRange])
  useEffect(() => setCustomStart(salesStart || ''), [salesStart])
  useEffect(() => setCustomEnd(salesEnd || ''), [salesEnd])

  const activeRange = customMode || salesRange === 'custom' ? 'custom' : salesRange
  const hasAppliedCustomRange =
    salesRange === 'custom' && Boolean(salesStart) && Boolean(salesEnd)

  const chartData = (data?.labels || []).map((label, i) => ({
    label,
    sales: data?.sales?.[i] || 0,
    orders: data?.orders?.[i] || 0,
  }))

  const handlePresetChange = (value) => {
    if (value === 'custom') {
      setCustomMode(true)
      return
    }
    setCustomMode(false)
    onRangeChange(value)
  }

  return (
    <Paper sx={dashboardCardSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <BarChartOutlinedIcon sx={{ fontSize: 20, color: DASHBOARD_COLORS.red }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Sales Overview</Typography>
            {timezone && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                {timezone}
              </Typography>
            )}
          </Box>
        </Stack>
        <RangeButtons
          value={activeRange}
          options={[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: 'Monthly' },
            { value: '90d', label: 'Quarterly' },
            { value: 'custom', label: 'Custom' },
          ]}
          onChange={handlePresetChange}
        />
      </Stack>
      {activeRange === 'custom' && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">From</Typography>
          <TextField
            type="date"
            size="small"
            value={customStart}
            onChange={(event) => setCustomStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" color="text.secondary">→ To</Typography>
          <TextField
            type="date"
            size="small"
            value={customEnd}
            onChange={(event) => setCustomEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!customStart || !customEnd || customStart > customEnd}
            onClick={() =>
              onCustomRangeChange({
                salesRange: 'custom',
                salesStart: customStart,
                salesEnd: customEnd,
              })
            }
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600 }}
          >
            Apply
          </Button>
        </Stack>
      )}
      {activeRange === 'custom' && !hasAppliedCustomRange && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Choose a start and end date, then click Apply.
        </Typography>
      )}
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <ChartLegend
        items={[
          { label: 'Sales', color: DASHBOARD_COLORS.red, fill: 'rgba(208,2,27,0.25)' },
          { label: 'Orders', color: DASHBOARD_COLORS.blue, fill: 'rgba(41,128,185,0.15)', border: DASHBOARD_COLORS.blue },
        ]}
      />
      <Box sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888882' }} />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: '#888882' }}
              domain={[0, 'auto']}
              allowDataOverflow
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: DASHBOARD_COLORS.blue }}
              domain={[0, 'auto']}
              allowDataOverflow
            />
            <Tooltip content={<SalesOverviewTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar
              yAxisId="left"
              dataKey="sales"
              baseValue={0}
              fill="rgba(208,2,27,0.18)"
              stroke={DASHBOARD_COLORS.red}
              strokeWidth={1.5}
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId="right"
              dataKey="orders"
              baseValue={0}
              fill="rgba(41,128,185,0.15)"
              stroke={DASHBOARD_COLORS.blue}
              strokeWidth={1.5}
              radius={[4, 4, 0, 0]}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  )
}

export const TopEntitiesPanel = ({
  title,
  data,
  range,
  onRangeChange,
  loading,
  variant = 'outlet',
  orderStatus,
  orderStatusLoading,
}) => {
  const barColors =
    variant === 'partner' ? DASHBOARD_COLORS.partnerBars : DASHBOARD_COLORS.outletBars
  const TitleIcon = variant === 'partner' ? StorefrontOutlinedIcon : StoreOutlinedIcon
  const iconColor = variant === 'partner' ? DASHBOARD_COLORS.amber : DASHBOARD_COLORS.red

  return (
    <Paper sx={{ ...dashboardCardSx, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25, gap: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <TitleIcon sx={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '13.5px' }}>
            {title}
          </Typography>
        </Stack>
        <RangeButtons
          value={range}
          compact
          options={[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: 'Monthly' },
            { value: '90d', label: 'Quarterly' },
          ]}
          onChange={onRangeChange}
        />
      </Stack>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Stack spacing={1.25}>
        {(data?.items || []).map((item, index) => (
          <Box key={item.name}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" fontWeight={600} noWrap sx={{ pr: 1 }}>
                {item.name}
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                {formatCurrency(item.value)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={item.pct || 0}
              sx={{
                mt: 0.5,
                height: 7,
                borderRadius: 99,
                bgcolor: DASHBOARD_COLORS.bg,
                '& .MuiLinearProgress-bar': {
                  bgcolor: barColors[Math.min(index, barColors.length - 1)],
                  borderRadius: 99,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
              {item.orders} orders
            </Typography>
          </Box>
        ))}
      </Stack>
      {variant === 'partner' && (
        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: DASHBOARD_COLORS.border }}>
          <OrderStatusBlock data={orderStatus} loading={orderStatusLoading} compact />
        </Box>
      )}
    </Paper>
  )
}

/** @deprecated Use OrderStatusBlock inside TopEntitiesPanel */
export const OrderStatusDonut = ({ data, loading }) => (
  <Paper sx={dashboardCardSx}>
    <OrderStatusBlock data={data} loading={loading} />
  </Paper>
)
